import { computed, del, ref, set } from "vue";
import { getConnectionId, useConnectionStore } from "@/stores/workflowConnectionStore";
import { assertDefined } from "@/utils/assertions";
import { defineScopedStore } from "./scopedStore";
import { useWorkflowStateStore } from "./workflowEditorStateStore";
export const useWorkflowStepStore = defineScopedStore("workflowStepStore", (workflowId) => {
    const steps = ref({});
    const stepMapOver = ref({});
    const stepInputMapOver = ref({});
    const stepIndex = ref(-1);
    const stepExtraInputs = ref({});
    function $reset() {
        steps.value = {};
        stepMapOver.value = {};
        stepInputMapOver.value = {};
        stepIndex.value = -1;
        stepExtraInputs.value = {};
    }
    const getStep = computed(() => (stepId) => steps.value[stepId.toString()]);
    const getStepExtraInputs = computed(() => (stepId) => stepExtraInputs.value[stepId] || []);
    const getStepIndex = computed(() => Math.max(...Object.values(steps.value).map((step) => step.id), stepIndex.value));
    const hasActiveOutputs = computed(() => Boolean(Object.values(steps.value).find((step) => { var _a; return (_a = step.workflow_outputs) === null || _a === void 0 ? void 0 : _a.length; })));
    const workflowOutputs = computed(() => {
        const workflowOutputs = {};
        Object.values(steps.value).forEach((step) => {
            var _a;
            if ((_a = step.workflow_outputs) === null || _a === void 0 ? void 0 : _a.length) {
                step.workflow_outputs.forEach((workflowOutput) => {
                    if (workflowOutput.label) {
                        workflowOutputs[workflowOutput.label] = {
                            outputName: workflowOutput.output_name,
                            stepId: step.id,
                        };
                    }
                });
            }
        });
        return workflowOutputs;
    });
    const duplicateLabels = computed(() => {
        const duplicateLabels = new Set();
        const labels = new Set();
        Object.values(steps.value).forEach((step) => {
            var _a;
            if ((_a = step.workflow_outputs) === null || _a === void 0 ? void 0 : _a.length) {
                step.workflow_outputs.forEach((workflowOutput) => {
                    if (workflowOutput.label) {
                        if (labels.has(workflowOutput.label)) {
                            duplicateLabels.add(workflowOutput.label);
                        }
                        labels.add(workflowOutput.label);
                    }
                });
            }
        });
        return duplicateLabels;
    });
    const connectionStore = useConnectionStore(workflowId);
    const stateStore = useWorkflowStateStore(workflowId);
    function addStep(newStep, select = false, createConnections = true) {
        var _a;
        const stepId = (_a = newStep.id) !== null && _a !== void 0 ? _a : getStepIndex.value + 1;
        const step = Object.freeze(Object.assign(Object.assign({}, newStep), { id: stepId }));
        set(steps.value, stepId.toString(), step);
        if (createConnections) {
            stepToConnections(step).forEach((connection) => connectionStore.addConnection(connection));
        }
        stepExtraInputs.value[step.id] = findStepExtraInputs(step);
        if (select) {
            stateStore.setStepMultiSelected(step.id, true);
        }
        return step;
    }
    function insertNewStep(contentId, name, type, position) {
        const stepData = {
            name: name,
            content_id: contentId,
            input_connections: {},
            type: type,
            inputs: [],
            outputs: [],
            position: position,
            post_job_actions: {},
            tool_state: {},
        };
        return addStep(stepData);
    }
    function updateStep(step) {
        var _a;
        const workflow_outputs = (_a = step.workflow_outputs) === null || _a === void 0 ? void 0 : _a.filter((workflowOutput) => step.outputs.find((output) => workflowOutput.output_name == output.name));
        steps.value[step.id.toString()] = Object.freeze(Object.assign(Object.assign({}, step), { workflow_outputs }));
        stepExtraInputs.value[step.id] = findStepExtraInputs(step);
    }
    function updateStepValue(stepId, key, value) {
        const step = steps.value[stepId];
        assertDefined(step);
        const partialStep = {};
        partialStep[key] = value;
        updateStep(Object.assign(Object.assign({}, step), partialStep));
    }
    function changeStepMapOver(stepId, mapOver) {
        set(stepMapOver.value, stepId, mapOver);
    }
    function resetStepInputMapOver(stepId) {
        set(stepInputMapOver.value, stepId, {});
    }
    function changeStepInputMapOver(stepId, inputName, mapOver) {
        if (stepInputMapOver.value[stepId]) {
            set(stepInputMapOver.value[stepId], inputName, mapOver);
        }
        else {
            set(stepInputMapOver.value, stepId, { [inputName]: mapOver });
        }
    }
    function addConnection(connection) {
        const inputStep = getStep.value(connection.input.stepId);
        assertDefined(inputStep, `Failed to add connection, because step with id ${connection.input.stepId} is undefined`);
        const input = inputStep.inputs.find((input) => input.name === connection.input.name);
        const connectionLink = {
            output_name: connection.output.name,
            id: connection.output.stepId,
        };
        if (input && "input_subworkflow_step_id" in input && input.input_subworkflow_step_id !== undefined) {
            connectionLink["input_subworkflow_step_id"] = input.input_subworkflow_step_id;
        }
        let connectionLinks = [connectionLink];
        let inputConnection = inputStep.input_connections[connection.input.name];
        if (inputConnection) {
            if (!Array.isArray(inputConnection)) {
                inputConnection = [inputConnection];
            }
            inputConnection = inputConnection.filter((connection) => !(connection.id === connectionLink.id && connection.output_name === connectionLink.output_name));
            connectionLinks = [...connectionLinks, ...inputConnection];
        }
        const updatedStep = Object.assign(Object.assign({}, inputStep), { input_connections: Object.assign(Object.assign({}, inputStep.input_connections), { [connection.input.name]: connectionLinks.sort((a, b) => a.id === b.id ? a.output_name.localeCompare(b.output_name) : a.id - b.id) }) });
        updateStep(updatedStep);
    }
    function removeConnection(connection) {
        const inputStep = getStep.value(connection.input.stepId);
        assertDefined(inputStep, `Failed to remove connection, because step with id ${connection.input.stepId} is undefined`);
        const inputConnections = inputStep.input_connections[connection.input.name];
        if (getStepExtraInputs.value(inputStep.id).find((input) => connection.input.name === input.name)) {
            inputStep.input_connections[connection.input.name] = undefined;
        }
        else {
            if (Array.isArray(inputConnections)) {
                inputStep.input_connections[connection.input.name] = inputConnections.filter((outputLink) => !(outputLink.id === connection.output.stepId, outputLink.output_name === connection.output.name));
            }
            else {
                del(inputStep.input_connections, connection.input.name);
            }
        }
        updateStep(inputStep);
    }
    const { deleteStepPosition, deleteStepTerminals } = useWorkflowStateStore(workflowId);
    function removeStep(stepId) {
        connectionStore
            .getConnectionsForStep(stepId)
            .forEach((connection) => connectionStore.removeConnection(getConnectionId(connection)));
        del(steps.value, stepId.toString());
        del(stepExtraInputs.value, stepId);
        del(stateStore.multiSelectedSteps, stepId);
        del(stepMapOver.value, stepId.toString());
        deleteStepPosition(stepId);
        deleteStepTerminals(stepId);
    }
    return {
        steps,
        stepMapOver,
        stepInputMapOver,
        stepIndex,
        stepExtraInputs,
        $reset,
        getStep,
        getStepExtraInputs,
        getStepIndex,
        hasActiveOutputs,
        workflowOutputs,
        duplicateLabels,
        addStep,
        insertNewStep,
        updateStep,
        updateStepValue,
        changeStepMapOver,
        resetStepInputMapOver,
        changeStepInputMapOver,
        addConnection,
        removeConnection,
        removeStep,
    };
});
function makeConnection(inputId, inputName, outputId, outputName) {
    return {
        input: {
            stepId: inputId,
            name: inputName,
            connectorType: "input",
        },
        output: {
            stepId: outputId,
            name: outputName,
            connectorType: "output",
        },
    };
}
function stepToConnections(step) {
    const connections = [];
    if (step.input_connections) {
        Object.entries(step === null || step === void 0 ? void 0 : step.input_connections).forEach(([inputName, outputArray]) => {
            if (outputArray === undefined) {
                return;
            }
            if (!Array.isArray(outputArray)) {
                outputArray = [outputArray];
            }
            outputArray.forEach((output) => {
                const connection = makeConnection(step.id, inputName, output.id, output.output_name);
                const connectionInput = step.inputs.find((input) => input.name == inputName);
                if (connectionInput && "input_subworkflow_step_id" in connectionInput) {
                    connection.input.input_subworkflow_step_id = connectionInput.input_subworkflow_step_id;
                }
                connections.push(connection);
            });
        });
    }
    return connections;
}
function findStepExtraInputs(step) {
    const extraInputs = [];
    if (step.when !== undefined) {
        Object.keys(step.input_connections).forEach((inputName) => {
            var _a;
            if (!step.inputs.find((input) => input.name === inputName) && ((_a = step.when) === null || _a === void 0 ? void 0 : _a.includes(inputName))) {
                const terminalSource = {
                    name: inputName,
                    optional: false,
                    input_type: "parameter",
                    type: "boolean",
                    multiple: false,
                    label: inputName,
                    extensions: [],
                };
                extraInputs.push(terminalSource);
            }
        });
    }
    return extraInputs;
}
